import { useLingui } from '@lingui/react/macro';
import { cn, Dropdown, toast } from '@utima/ui';
import { memo, useState } from 'react';

import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';

import { useFolderDefaultPriorityTooltip } from './FolderDefaultPriorityHint';
import { PriorityIndicator } from './PriorityIndicator';
import {
  PRIORITY_SCALES,
  priorityScaleToWeight,
  weightToPriorityScale,
  type PriorityScale,
} from '../lib/priority';

type InlinePriorityEditorProps = {
  weight: number;
  variant?: 'default' | 'muted';
  onChange: (weight: number) => Promise<void>;
  disabled?: boolean;
};

export const InlinePriorityEditor = memo(function InlinePriorityEditor({
  weight,
  variant = 'default',
  onChange,
  disabled = false,
}: InlinePriorityEditorProps) {
  const { t } = useLingui();
  const folderDefaultPriorityTooltip = useFolderDefaultPriorityTooltip();
  const [isSaving, setIsSaving] = useState(false);
  const currentScale = weightToPriorityScale(weight);

  const handleSelect = async (scale: PriorityScale) => {
    const nextWeight = priorityScaleToWeight(scale);

    if (nextWeight === weight) {
      return;
    }

    setIsSaving(true);

    try {
      await onChange(nextWeight);
    } catch {
      toast.error(t`Failed to update priority`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dropdown.Root modal={false}>
      <Dropdown.Trigger asChild disabled={disabled || isSaving}>
        <button
          type='button'
          data-table='prevent'
          className={cn(
            'rounded-md px-1 py-0.5 transition-colors',
            'hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            (disabled || isSaving) && 'pointer-events-none opacity-50',
          )}
          onClick={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
        >
          {variant === 'muted' ? (
            <SimpleTooltip title={folderDefaultPriorityTooltip}>
              <span className='inline-flex'>
                <PriorityIndicator weight={weight} variant={variant} />
              </span>
            </SimpleTooltip>
          ) : (
            <PriorityIndicator weight={weight} variant={variant} />
          )}
        </button>
      </Dropdown.Trigger>
      <Dropdown.Content
        side='bottom'
        align='start'
        className='min-w-[168px]'
        onClick={event => event.stopPropagation()}
      >
        {PRIORITY_SCALES.map(scale => (
          <Dropdown.Item
            key={scale}
            className={cn(
              'flex items-center p-2',
              scale === currentScale && 'bg-muted/60',
            )}
            onClick={event => {
              event.stopPropagation();
              void handleSelect(scale);
            }}
          >
            <PriorityIndicator scale={scale} variant={variant} />
          </Dropdown.Item>
        ))}
      </Dropdown.Content>
    </Dropdown.Root>
  );
});
